import type { ErrorEvent, EventHint } from '@sentry/core';
import { isAxiosError } from 'axios';

import { addAxiosContextRecursive, enrichAxiosError } from './enrichAxiosError';
import type { TSentryBeforeSendHook } from './types';

interface IOptions {
	filteredStatusCodes?: number[];
	customFilter?: (event: ErrorEvent, hint: EventHint) => boolean;
}

/**
 * Creates a Sentry beforeSend hook that enriches Axios errors
 * and optionally filters by HTTP status codes
 *
 * @param filteredStatusCodes - HTTP status codes to filter (e.g., [401])
 *                              Empty array by default - caller decides what to filter
 * @param customFilter - Optional additional filter function
 * @returns Sentry beforeSend hook function
 */
export function createSentryBeforeSend({
	filteredStatusCodes = [],
	customFilter,
}: IOptions = {}): TSentryBeforeSendHook {
	return (event: ErrorEvent, hint: EventHint) => {
		const error = hint?.originalException;

		/** Apply custom filter first if provided */
		if (customFilter && !customFilter(event, hint)) {
			return null;
		}

		/** Handle AxiosError enrichment and filtering */
		if (error && isAxiosError(error)) {
			const status = error.response?.status;

			/** Filter out specified HTTP status codes if any */
			if (
				status &&
				filteredStatusCodes.length > 0 &&
				filteredStatusCodes.includes(status)
			) {
				return null;
			}

			/** Add rich context for debugging */
			addAxiosContextRecursive(event, error);
			enrichAxiosError(event, error);
		}

		return event;
	};
}
